import React, { useMemo } from 'react';
import TotalScore from './TotalScore';
import ProgressBarComponent from './ProgressBarComponent';
import ParameterIcon from './ParameterIcon';
import { calculateSustainabilityAverages } from '../utils/sustainabilityAverages';
import getColor from '../utils/interpolateColor';

const toComponentShape = (product) => ({
  ...product,
  co2e_equivalent: product.co2e_equivalent ?? null,
  climate_score: product.climate_score ?? 0,
  water_score: product.water_score ?? 0,
  health_score: product.health_score ?? 0,
  biodiversity_score: product.biodiversity_score ?? 0,
  land_use_score: product.land_use_score ?? 0,
  transport_score: product.transport_score ?? 0,
  fertilizers_use_score: product.fertilizers_use_score ?? 0,
  pesticides_use_score: product.pesticides_use_score ?? 0,
  antibiotics_resistance_score: product.antibiotics_resistance_score ?? 0,
  social_justice_score: product.social_justice_score ?? 0,
  child_labor_score: product.child_labor_score ?? 0,
  equality_score: product.equality_score ?? 0,
  worker_rights_score: product.worker_rights_score ?? 0,
  local_population_score: product.local_population_score ?? 0,
  animal_protection_score: product.animal_protection_score ?? 0,
});

const convertPackToKgOrL = (size, unitId) => {
  if (!size || !unitId) return 0;
  switch (Number(unitId)) {
    case 2: // KGM
    case 6: // LTR
    case 1: // styck
      return Number(size);
    case 3: // GRM
    case 5: // MLT
      return Number(size) / 1000;
    case 4: // MGM
      return Number(size) / 1000000;
    case 11: // DLT
      return Number(size) / 10;
    default:
      return 0;
  }
};

const PARAM_GROUPS = [
  {
    title: 'Miljö',
    items: [
      { name: 'Klimat', key: 'climate_score', bonusKey: 'climate_bonus' },
      { name: 'Vatten', key: 'water_score', bonusKey: 'water_bonus' },
      { name: 'Biologisk mångfald', key: 'biodiversity_score', bonusKey: 'biodiversity_bonus' },
      { name: 'Transport', key: 'transport_score', bonusKey: 'transport_bonus' },
      { name: 'Landanvändning', key: 'land_use_score', bonusKey: 'land_use_bonus' },
      { name: 'Övergödning', key: 'fertilizers_use_score', bonusKey: 'fertilizers_use_bonus' },
      { name: 'Bekämpningsmedel', key: 'pesticides_use_score', bonusKey: 'pesticides_use_bonus' },
    ],
  },
  {
    title: 'Hälsa',
    items: [
      { name: 'Hälsa', key: 'health_score', bonusKey: 'health_bonus' },
      { name: 'Antibiotikaresistens', key: 'antibiotics_resistance_score', bonusKey: 'antibiotics_resistance_bonus' },
    ],
  },
  {
    title: 'Rättvisa',
    items: [
      { name: 'Social rättvisa', key: 'social_justice_score', bonusKey: 'social_justice_bonus' },
      { name: 'Barnarbete', key: 'child_labor_score', bonusKey: 'child_labor_bonus' },
      { name: 'Jämlikhet', key: 'equality_score', bonusKey: 'equality_bonus' },
      { name: 'Arbetsförhållanden', key: 'worker_rights_score', bonusKey: 'worker_rights_bonus' },
      { name: 'Lokalbefolkning', key: 'local_population_score', bonusKey: 'local_population_bonus' },
      { name: 'Djurvälfärd', key: 'animal_protection_score', bonusKey: 'animal_protection_bonus' },
    ],
  },
];

/** Compact ring matching Core CompareParameters tile style */
/** Core-like tile: icon inside ring + label/score beside (CompareParameters) */
const ScoreTile = ({ label, score, t, healthPlus = false }) => {
  const value = Math.max(0, Math.min(100, Number(score) || 0));
  const size = 52;
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  const color = getColor(value);
  const iconSize = label === 'Hälsa' ? (healthPlus ? 22 : 26) : 24;

  return (
    <div className="flex items-center justify-center gap-2 bg-gray-100 rounded-lg py-3 px-2">
      <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="absolute inset-0">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#c9d1d0" strokeWidth={stroke} />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={value > 0 ? color : 'none'}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <ParameterIcon
            title={label}
            width={iconSize}
            height={iconSize}
            healthPlus={healthPlus}
          />
        </div>
      </div>
      <div className="flex flex-col justify-center leading-tight min-w-0">
        <p className="m-0 text-xs text-gray-700 truncate">{t(label)}</p>
        <p className="m-0 text-sm font-semibold" style={{ color }}>{value}</p>
      </div>
    </div>
  );
};

/**
 * Portal compare card styled like Core CompareProductsCard.
 */
const ProductCompareCard = ({
  product,
  isCurrent,
  onAdd,
  onRemove,
  adding,
  t,
}) => {
  const component = useMemo(() => toComponentShape(product), [product]);

  const averages = useMemo(() => {
    const calc = calculateSustainabilityAverages(component);
    const social =
      (typeof component.social_average === 'number' && component.social_average > 0
        ? component.social_average
        : null)
      ?? (typeof component.fairness_score === 'number' && component.fairness_score > 0
        ? component.fairness_score
        : null)
      ?? calc.social_average;

    return {
      environment_average:
        (typeof component.environment_average === 'number' && component.environment_average > 0
          ? component.environment_average
          : null) ?? calc.environment_average,
      health_average:
        (typeof component.health_average === 'number' && component.health_average > 0
          ? component.health_average
          : null) ?? calc.health_average,
      social_average: social,
    };
  }, [component]);

  const co2ePerKg = useMemo(() => {
    const v = parseFloat(component.co2e_equivalent);
    return Number.isFinite(v) ? v : null;
  }, [component.co2e_equivalent]);

  const co2eTotal = useMemo(() => {
    if (co2ePerKg == null) return null;
    const pack = convertPackToKgOrL(product.size, product.default_unit_id);
    if (!pack) return null;
    return (pack * co2ePerKg).toFixed(2);
  }, [co2ePerKg, product.size, product.default_unit_id]);

  const packLabel = [product.size, product.default_unit_abbr].filter(Boolean).join(' ');

  return (
    <div className="relative w-full min-w-0 bg-white shadow-md rounded-lg p-3 flex flex-col h-full border border-gray-100">
      {!isCurrent && onRemove && (
        <button
          type="button"
          onClick={() => onRemove(product)}
          className="absolute top-2 right-2 z-10 p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          aria-label={t('Ta bort från jämförelse')}
          title={t('Ta bort från jämförelse')}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
      <div className="grid grid-cols-3 gap-2 pb-2">
        <div className="col-span-1 relative flex items-center justify-center">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name || ''}
              className="w-full h-28 object-contain rounded-md"
            />
          ) : (
            <div className="w-full h-28 bg-gray-100 flex items-center justify-center rounded-md text-xs text-gray-400">
              {t('Ingen bild')}
            </div>
          )}
          {product.country_alpha2 && (
            <div className="absolute -top-1 -left-1 flex flex-col items-center" title={product.country_name || product.country_alpha2}>
              {product.country_name && (
                <span className="text-[9px] leading-tight text-gray-600 mb-0.5 max-w-[3.5rem] truncate text-center">
                  {product.country_name}
                </span>
              )}
              <img
                src={`https://flagcdn.com/48x36/${String(product.country_alpha2).toLowerCase()}.png`}
                alt={product.country_name || product.country_alpha2}
                height={28}
                className="h-7 w-auto shadow-md rounded-sm bg-white"
              />
            </div>
          )}
        </div>
        <div className="col-span-2 flex flex-col items-center justify-center text-center min-w-0 px-1 pr-5">
          {product.gtin && (
            <p className="text-xs text-gray-500 mb-0.5 font-mono truncate w-full">{product.gtin}</p>
          )}
          <h3 className="text-base font-bold text-gray-900 mb-1 line-clamp-2 leading-snug" title={product.name}>
            {product.name}
          </h3>
          <div className="flex flex-wrap gap-1 justify-center items-center text-xs text-gray-600">
            {product.brand && <span className="truncate max-w-[8rem]">{product.brand}</span>}
            {product.brand && packLabel ? <span className="text-gray-400">·</span> : null}
            {packLabel ? <span>{packLabel}</span> : null}
          </div>
          {Boolean(product.is_eco) && (
            <div className="mt-1.5 flex items-center justify-center gap-1" title={t('Ekologisk')}>
              <img
                src={`${process.env.PUBLIC_URL || ''}/assets/icons/svg/Eko.svg`}
                alt={t('Ekologisk')}
                className="h-6 w-6 object-contain"
              />
              <span className="text-[10px] font-medium text-green-800">{t('Ekologisk')}</span>
            </div>
          )}
          {isCurrent && (
            <span className="inline-block mt-1.5 text-[10px] uppercase tracking-wide font-semibold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">
              {t('Nuvarande')}
            </span>
          )}
        </div>
      </div>

      <div
        className="flex justify-center items-center rounded-full px-2 py-2 mb-2 w-full"
        style={{ border: '1px solid #E5E7EB' }}
      >
        <div className="flex flex-col items-center px-2">
          <p className="text-xs text-gray-500 m-0 text-center">
            {t('CO2e kg/kg')}: {co2ePerKg != null ? Number(co2ePerKg).toFixed(2) : t('N/A')}
          </p>
        </div>
        <div className="bg-gray-300 w-px h-8 mx-2 rounded-full flex-shrink-0" />
        <div className="flex flex-col items-center px-2">
          <p className="text-xs text-gray-500 m-0 text-center">
            {t('TOTAL CO2e kg')}: {co2eTotal ?? t('N/A')}
          </p>
        </div>
      </div>

      <div className="bg-gray-50 rounded-md px-3 py-3 mb-2">
        <div className="flex justify-between items-center w-full gap-2 mb-3">
          <img
            src={`${process.env.PUBLIC_URL || ''}/assets/images/consupedia_logo_blue.svg`}
            alt="Consupedia"
            className="h-6 w-auto max-w-[45%] object-contain"
          />
          <TotalScore
            dish={{
              environment_average: averages.environment_average,
              health_average: averages.health_average,
              social_average: averages.social_average,
            }}
            size="xs"
          />
        </div>
        <div className="grid grid-cols-2 gap-2 w-full">
          <ScoreTile label="Miljö" score={averages.environment_average} t={t} />
          <ScoreTile label="Hälsa" score={averages.health_average} t={t} healthPlus />
          <ScoreTile label="Rättvisa" score={averages.social_average} t={t} />
          <ScoreTile label="Transparens" score={product.data_quality} t={t} />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto max-h-56 pr-0.5 space-y-3">
        {PARAM_GROUPS.map((group) => (
          <div key={group.title}>
            <h4 className="text-xs font-bold text-gray-700 text-center mb-1.5">{t(group.title)}</h4>
            {group.items.map((item) => (
              <ProgressBarComponent
                key={item.key}
                name={item.name}
                score={Number(component[item.key]) || 0}
                bonus={product[item.bonusKey] ?? null}
                isIcon
                t={t}
              />
            ))}
          </div>
        ))}
      </div>

      {!isCurrent && (
        <button
          type="button"
          disabled={adding}
          onClick={() => onAdd(product)}
          className="mt-3 w-full px-3 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {adding ? `${t('Laddar')}...` : t('Lägg till')}
        </button>
      )}
    </div>
  );
};

export default ProductCompareCard;
